import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Col, CardHeader, Row } from 'reactstrap';
import ReactHighChart from 'react-highcharts/ReactHighcharts.src';

import ChartReportApi from 'apis/reports/ChartReport';
import Filters from './Filters';
import { generateChartConfig } from 'utils/generateChartConfig';
import Legends from './Legends';
import ChartSkeleton from '../skeleton-uis/charts/ChartSkeleton';
import NoSurveyTemplateError from './NoSurveyTemplateError';

const initialState = {
  isSurveyTemplateQuestion: true,
  isLoading: true,
  filter: '',
  filters: null,
  errorText: '',
  series: {
    xAxis: [],
    businessData: [],
    averageData: [],
  },
};

class ChartReport extends Component {
  constructor() {
    super();
    this.state = {
      ...initialState,
    };
  }

  componentWillMount = () => {
    const { filters } = this.props;

    if (filters) {
      this.setState({ filter: filters[0] }, this.updateChart);
    } else {
      this.updateChart();
    }
  };

  componentDidUpdate = prevProps => {
    const { startDate, endDate } = this.props;
    if (startDate !== prevProps.startDate || endDate !== prevProps.endDate) {
      if ((startDate && endDate) || (!startDate && !endDate)) {
        this.updateChart();
      }
    }
  };

  updateChart = () => {
    const {
      startDate,
      endDate,
      questionId,
      category,
      filterType,
      dataType,
    } = this.props;
    const { filter } = this.state;
    const start_date = startDate;
    const end_date = endDate;
    let filters = { type: 'all' };
    if (filterType) {
      filters = {};
      filters[filterType] = filter;
    }

    this.setState({ isLoading: true });

    ChartReportApi.getAll(
      questionId,
      filters,
      category,
      dataType,
      start_date,
      end_date
    )
      .then(({ data }) => {
        if (typeof data === 'string') {
          this.setState({
            ...initialState,
            isSurveyTemplateQuestion: false,
            isLoading: false,
            errorText: data,
          });
          return;
        }
        this.setState(prevState => ({
          series: data,
          isLoading: false,
          isSurveyTemplate: true,
        }));
      })
      .catch(e => {
        console.log(e);
        this.setState({ isLoading: false });
      });
  };

  onFilter = filterValue => {
    const { filter } = this.state;
    if (filterValue != filter) {
      this.setState({ filter: filterValue }, this.updateChart);
    }
  };

  renderChartBody = () => {
    const {
      isLoading,
      filter,
      series,
      isSurveyTemplateQuestion,
      errorText,
    } = this.state;
    const {
      titleText,
      subtitleText,
      chartType,
      filters,
      stacked,
      colors,
      axisTitle,
      percentage,
      height,
    } = this.props;
    const { xAxis, businessData, averageData } = series;
    const businessSeriesData = { xAxis, series: businessData };
    const averageSeriesData = { xAxis, series: averageData };
    const chartConfig = generateChartConfig({
      seriesData: averageSeriesData,
      chartType,
      titleText: '',
      subtitleText: '',
      yAxisTitle: axisTitle,
      stacked,
      colors,
      percentage,
      height,
    });

    const legendItems = businessData.map(data => data.name);

    const avgDataConfig = {
      ...chartConfig,
      legend: {
        enabled: false,
      },
      xAxis: {
        ...chartConfig.xAxis,
        labels: { enabled: false },
        opposite: true,
      },
      yAxis: {
        ...chartConfig.yAxis,
        labels: {
          formatter: function() {
            return Math.abs(this.value);
          },
        },
      },
    };

    let businessDataConfig = generateChartConfig({
      seriesData: businessSeriesData,
      chartType,
      titleText: '',
      subtitleText: '',
      yAxisTitle: axisTitle,
      stacked,
      colors,
      percentage,
      height,
    });

    businessDataConfig = {
      ...businessDataConfig,
      chart: { ...businessDataConfig.chart, marginRight: 20 },
      yAxis: {
        ...businessDataConfig.yAxis,
        title: { text: '' },
      },
      legend: {
        enabled: false,
      },
    };

    if (isLoading) {
      return <ChartSkeleton chartType={chartType} />;
    }

    return (
      <Fragment>
        <NoSurveyTemplateError
          isSurveyTemplateQuestion={isSurveyTemplateQuestion}
          errorText={errorText}
        />
        <Row>
          <Legends legendItems={legendItems} colors={colors} />
        </Row>
        <Row className={!isSurveyTemplateQuestion ? 'invisible' : ''}>
          <Col xs={2} xl={2} sm={2} md={2} lg={2} className="p-0 my-0 avg-text">
            Average Participant
          </Col>
          <Col xs={4} xl={4} sm={4} md={4} lg={4} className="p-0 my-0">
            <ReactHighChart config={avgDataConfig} />
          </Col>
          <Col
            xs={5}
            xl={5}
            sm={5}
            md={5}
            lg={5}
            className="p-0 my-0"
            style={{ position: 'relative', left: '-10px' }}
          >
            <ReactHighChart config={businessDataConfig} />
          </Col>
          <Col
            xs={1}
            xl={1}
            sm={1}
            md={1}
            lg={1}
            className="p-0 my-0 your-text"
          >
            Your Participants
          </Col>
        </Row>
        {filters && (
          <span className="filter-container">
            <Filters
              filters={filters}
              onFilter={this.onFilter}
              filter={filter}
            />
          </span>
        )}
      </Fragment>
    );
  };

  renderChartTitle = () => {
    const { isLoading, isSurveyTemplateQuestion } = this.state;
    const { titleText, subtitleText } = this.props;

    if (isLoading || !isSurveyTemplateQuestion) return null;

    return (
      <Fragment>
        <div
          style={{
            color: '#29508B',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {titleText}
        </div>
        <div
          style={{
            color: '#29508B',
            fontSize: '12px',
          }}
        >
          {subtitleText}
        </div>
      </Fragment>
    );
  };

  render() {
    const { headTitle } = this.props;

    return (
      <Row className="w-100">
        <Col className="p-0 my-0">
          <Card className="rounded-0">
            {headTitle && (
              <CardHeader className="chart-group-head text-center">
                {headTitle}
              </CardHeader>
            )}
            <CardBody>
              {this.renderChartTitle()}
              <Col>{this.renderChartBody()}</Col>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }
}

export default ChartReport;

ChartReport.propTypes = {
  filters: PropTypes.array,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  questionId: PropTypes.number,
  category: PropTypes.string,
  filterType: PropTypes.string,
  dataType: PropTypes.string,
  titleText: PropTypes.string,
  subtitleText: PropTypes.string,
  chartType: PropTypes.string,
  stacked: PropTypes.bool,
  colors: PropTypes.array,
  axisTitle: PropTypes.string,
  percentage: PropTypes.bool,
  legendConfig: PropTypes.object,
  headTitle: PropTypes.string,
  showGrids: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/charts/ButterFlyChart.js